import API_BASE_URL from "../../utils/apiBase";
import React, { useRef, useState, useEffect } from "react";
import {
  MdPhotoCamera,
  MdClose,
  MdCameraswitch,
  MdCloudUpload,
  MdCheckCircle,
  MdDeleteOutline,
  MdOpenInNew,
  MdErrorOutline,
} from "react-icons/md";
import "./WebCamCapture.css";

export default function WebcamCapture({ onUpload, onDelete }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [isStreaming, setIsStreaming] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fullScreen, setFullScreen] = useState(false);
  const [S3key, setS3key] = useState(null);

  useEffect(() => {
    return () => stopCamera();
  }, []);

  async function startCamera() {
    setError(null);
    try {
      stopCamera();
      setFullScreen(true);

      const constraints = {
        audio: false,
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      setIsStreaming(true);
    } catch (err) {
      setError(err.message || "Cannot access camera");
      setIsStreaming(false);
      setFullScreen(false);
    }
  }

  function stopCamera() {
    setIsStreaming(false);
    const s = streamRef.current;
    if (s) s.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      } catch (e) {}
    }
    setFullScreen(false);
  }

  function flipCamera() {
    setFacingMode((prev) => {
      const next = prev === "user" ? "environment" : "user";
      if (isStreaming) {
        stopCamera();
        setTimeout(() => startCamera(), 150);
      }
      return next;
    });
  }

  function capturePhoto() {
    if (!isStreaming) return setError("Camera is not running");
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    setPhotoDataUrl(canvas.toDataURL("image/png"));
    stopCamera();
  }

  async function uploadPhoto() {
    if (!photoDataUrl) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(photoDataUrl);
      const blob = await res.blob();
      const formData = new FormData();
      formData.append("file", blob, `photo_${Date.now()}.png`);

      const response = await fetch(`${API_BASE_URL}/api/v1/image/upload`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (data?.success) {
        setUploadedUrl(data.Location);
        setPhotoDataUrl(null);
        setS3key(data.Key);
        onUpload(data.Location);
        setFullScreen(false);
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function deletePhoto() {
    if (!uploadedUrl) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/image/delete`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: S3key }),
      });

      const data = await response.json();
      if (data?.success) {
        setUploadedUrl(null);
        onDelete();
      } else {
        throw new Error("Delete failed");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Markup below is presentation only: the conditions and handlers are the
  // same as before. The viewfinder now renders inline in the form instead of
  // a full-screen portal, and the controls sit under the picture rather than
  // on top of it.
  return (
    <div className="webcam">
      {!fullScreen && !uploadedUrl && (
        <button type="button" onClick={startCamera} className="webcam-start">
          <span className="webcam-start-icon">
            <MdPhotoCamera />
          </span>
          <span className="webcam-start-text">
            <strong>{photoDataUrl ? "Retake photo" : "Take visitor photo"}</strong>
            <small>Required before submitting</small>
          </span>
        </button>
      )}

      {fullScreen && (
        <div className="webcam-camera">
          <div className="webcam-stage">
            <video
              ref={videoRef}
              className="webcam-video"
              playsInline
              muted
              autoPlay
              onClick={capturePhoto}
              onTouchStart={capturePhoto}
            />

            {/* Framing guide — decorative, does not affect the captured image */}
            <div className="webcam-guide" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </div>

            {isStreaming ? (
              <span className="webcam-live">Live</span>
            ) : (
              <div className="webcam-status">Starting camera…</div>
            )}
          </div>

          <div className="webcam-controls">
            <button
              type="button"
              onClick={stopCamera}
              className="webcam-icon-btn"
              aria-label="Close camera"
              title="Close camera"
            >
              <MdClose />
            </button>
            <button
              type="button"
              onClick={capturePhoto}
              className="webcam-shutter"
              aria-label="Capture photo"
              title="Capture photo"
            >
              <span />
            </button>
            <button
              type="button"
              onClick={flipCamera}
              className="webcam-icon-btn"
              aria-label="Switch camera"
              title="Switch camera"
            >
              <MdCameraswitch />
            </button>
          </div>
          <p className="webcam-hint">Tap the button or the picture to capture</p>
        </div>
      )}

      {photoDataUrl && !uploadedUrl && (
        <div className="webcam-result">
          <div className="webcam-result-media">
            <img src={photoDataUrl} alt="captured" className="webcam-img" />
            <span className="webcam-badge webcam-badge--pending">Not uploaded yet</span>
          </div>
          <div className="webcam-result-actions">
            <button
              type="button"
              onClick={uploadPhoto}
              disabled={loading}
              className="webcam-btn webcam-btn--primary"
            >
              <MdCloudUpload />
              {loading ? "Uploading…" : "Upload photo"}
            </button>
            <button
              type="button"
              onClick={() => setPhotoDataUrl(null)}
              className="webcam-btn webcam-btn--ghost"
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {uploadedUrl && (
        <div className="webcam-result webcam-result--done">
          <div className="webcam-result-media">
            <img src={uploadedUrl} alt="uploaded" className="webcam-img" />
            <span className="webcam-badge webcam-badge--done">
              <MdCheckCircle /> Photo uploaded
            </span>
          </div>
          <div className="webcam-result-actions">
            <a
              href={uploadedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="webcam-btn webcam-btn--ghost"
            >
              <MdOpenInNew /> View full size
            </a>
            <button
              type="button"
              onClick={deletePhoto}
              disabled={loading}
              className="webcam-btn webcam-btn--danger"
            >
              <MdDeleteOutline />
              {loading ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: "none" }} />
      {error && (
        <div className="webcam-error" role="alert">
          <MdErrorOutline /> {error}
        </div>
      )}
    </div>
  );
}
